import { ColorSwatches } from "@redotech/redo-model/order-tracking/tracking-page-definition";

export const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        r: parseInt(result[1], 16),
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        g: parseInt(result[2], 16),
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        b: parseInt(result[3], 16),
      }
    : null;
};

export const rgbToHsl = (r: number, g: number, b: number) => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0,
    s = 0;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return [h * 360, s * 100, l * 100];
};

export const hslToRgb = (h: number, s: number, l: number) => {
  h /= 360;
  s /= 100;
  l /= 100;

  const hueToRgb = (p: number, q: number, t: number) => {
    if (t < 0) {
      t += 1;
    }
    if (t > 1) {
      t -= 1;
    }
    if (t < 1 / 6) {
      return p + (q - p) * 6 * t;
    }
    if (t < 1 / 2) {
      return q;
    }
    if (t < 2 / 3) {
      return p + (q - p) * (2 / 3 - t) * 6;
    }
    return p;
  };

  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hueToRgb(p, q, h + 1 / 3);
    g = hueToRgb(p, q, h);
    b = hueToRgb(p, q, h - 1 / 3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
};

export const rgbToHex = (r: number, g: number, b: number) => {
  return (
    "#" +
    [r, g, b]
      .map((x) => {
        const hex = x.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("")
  );
};

export const hexToHsl = (hex: string) => {
  const rgb = hexToRgb(hex);
  if (!rgb) {
    return [0, 0, 0];
  }
  return rgbToHsl(rgb.r, rgb.g, rgb.b);
};

export const hslToHex = (h: number, s: number, l: number) => {
  const [r, g, b] = hslToRgb(h, s, l);
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  return rgbToHex(r, g, b);
};

const lerp = (start: number, end: number, t: number) => {
  return start + (end - start) * t;
};

const interpolateValue = (
  stop: number,
  anchors: { stop: number; value: number }[],
) => {
  let lowerAnchor = anchors[0];
  let upperAnchor = anchors[anchors.length - 1];

  for (let i = 0; i < anchors.length - 1; i++) {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    if (stop >= anchors[i].stop && stop <= anchors[i + 1].stop) {
      lowerAnchor = anchors[i];
      upperAnchor = anchors[i + 1];
      break;
    }
  }

  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  const t = (stop - lowerAnchor.stop) / (upperAnchor.stop - lowerAnchor.stop);
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  return lerp(lowerAnchor.value, upperAnchor.value, t);
};

interface ColorSwatch {
  stop: number;
  hex: string;
}

const createColorSwatchesFromPalette = (
  swatches: ColorSwatch[],
): ColorSwatches => ({
  50: swatches.find((p) => p.stop === 50)?.hex || "#000000",
  100: swatches.find((p) => p.stop === 100)?.hex || "#000000",
  200: swatches.find((p) => p.stop === 200)?.hex || "#000000",
  300: swatches.find((p) => p.stop === 300)?.hex || "#000000",
  400: swatches.find((p) => p.stop === 400)?.hex || "#000000",
  500: swatches.find((p) => p.stop === 500)?.hex || "#000000",
  600: swatches.find((p) => p.stop === 600)?.hex || "#000000",
  700: swatches.find((p) => p.stop === 700)?.hex || "#000000",
  800: swatches.find((p) => p.stop === 800)?.hex || "#000000",
  900: swatches.find((p) => p.stop === 900)?.hex || "#000000",
  950: swatches.find((p) => p.stop === 950)?.hex || "#000000",
});

export const createSwatches = (palette: {
  value: string;
  valueStop: number;
  h?: number;
  s?: number;
  lMin?: number;
  lMax?: number;
}): ColorSwatches => {
  const { value, valueStop } = palette;
  const h = palette.h ?? 0;
  const s = palette.s ?? 0;
  const lMin = palette.lMin ?? 0;
  const lMax = palette.lMax ?? 100;

  const allStops = [
    0, 50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950, 1000,
  ];
  const valueStopIndex = allStops.indexOf(valueStop);

  const inputHex = value.startsWith("#") ? value : `#${value}`;
  const rgb = hexToRgb(inputHex);
  if (!rgb) {
    throw new Error("Invalid hex color");
  }

  const [inputH, inputS, inputL] = rgbToHsl(rgb.r, rgb.g, rgb.b);
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  const normalizedInputH = isNaN(inputH) ? 0 : inputH;

  const hueScale = allStops.map((stop) => {
    const stopIndex = allStops.indexOf(stop);
    const diff = Math.abs(stopIndex - valueStopIndex);
    const tweakValue = h ? diff * h : 0;
    return { stop, tweak: tweakValue };
  });

  const saturationScale = allStops.map((stop) => {
    const stopIndex = allStops.indexOf(stop);
    const diff = Math.abs(stopIndex - valueStopIndex);
    const tweakValue = s ? Math.round((diff + 1) * s * (1 + diff / 10)) : 0;
    return { stop, tweak: Math.min(tweakValue, 100) };
  });

  const lightnessAnchors = [
    { stop: 0, value: lMax },
    { stop: valueStop, value: inputL },
    { stop: 1000, value: lMin },
  ];

  const swatches = allStops
    .filter((stop) => stop !== 0 && stop !== 1000)
    .map((stop) => {
      const hTweak = hueScale.find((h) => h.stop === stop)?.tweak || 0;
      const sTweak = saturationScale.find((s) => s.stop === stop)?.tweak || 0;
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      const lTweak = interpolateValue(stop, lightnessAnchors);

      let finalColor: string;

      if (stop === valueStop) {
        finalColor = inputHex;
      } else {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        const newH = (normalizedInputH + hTweak) % 360;
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        const newS = Math.max(0, Math.min(100, inputS + sTweak));
        const newL = Math.max(0, Math.min(100, lTweak));

        const [r, g, b] = hslToRgb(newH, newS, newL);
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        finalColor = rgbToHex(r, g, b);
      }

      return { stop, hex: finalColor };
    });

  return createColorSwatchesFromPalette(swatches);
};

export const getOptimalValueStop = (hexColor: string): number => {
  const rgb = hexToRgb(hexColor.startsWith("#") ? hexColor : `#${hexColor}`);
  if (!rgb) {
    return 500;
  }

  const [, , lightness] = rgbToHsl(rgb.r, rgb.g, rgb.b);

  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  if (lightness >= 80) {
    return 200;
  }
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  if (lightness >= 65) {
    return 300;
  }
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  if (lightness >= 55) {
    return 400;
  }
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  if (lightness >= 45) {
    return 500;
  }
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  if (lightness >= 35) {
    return 600;
  }
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  if (lightness >= 25) {
    return 700;
  }
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  if (lightness >= 15) {
    return 800;
  }
  return 900;
};

export const adjustColorBrightness = (
  hexColor: string,
  amount = 10,
): string => {
  const rgb = hexToRgb(hexColor.startsWith("#") ? hexColor : `#${hexColor}`);
  if (!rgb) {
    console.error(`Invalid hex color provided: ${hexColor}`);
    return "#000000";
  }

  const [h, s, l] = rgbToHsl(rgb.r, rgb.g, rgb.b);

  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  const isLight = l > 50;
  const adjustedL = isLight
    ? // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      Math.max(0, l - amount)
    : // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      Math.min(100, l + amount);

  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  return hslToHex(h, s, adjustedL);
};
